import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, Download, Mail, Info, Coins, HeartHandshake, ArrowRight } from "lucide-react";
import { api, API } from "../lib/api";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const CURRENCIES = ["AED", "USD", "EUR", "GBP", "INR", "SAR", "PKR", "BDT"];

const ASSET_GROUPS = [
  ["Cash & Liquid Assets", [["cash", "Cash in hand"], ["bank", "Bank balances"], ["savings", "Savings"], ["foreign_currency", "Foreign currency"], ["digital", "Digital balances"]]],
  ["Gold & Silver", [["gold_value", "Gold market value"], ["silver_value", "Silver market value"]]],
  ["Investments", [["shares", "Shares"], ["mutual_funds", "Mutual funds"], ["investments", "Other investment holdings"]]],
  ["Business Assets", [["business_inventory", "Inventory"], ["business_receivables", "Trade receivables"], ["business_cash", "Business cash"], ["other_business", "Other zakatable business assets"]]],
  ["Other Assets", [["other_assets", "Other zakatable assets"]]],
];

const LIABILITY_FIELDS = [["short_term", "Eligible short-term liabilities"], ["amounts_due", "Amounts due"]];

export default function ZakatCalculator() {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("AED");
  const [nisabBasis, setNisabBasis] = useState("gold");
  const [assets, setAssets] = useState({});
  const [liabilities, setLiabilities] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const payload = () => ({ currency, nisab_basis: nisabBasis, assets, liabilities });

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/zakat/calculate", payload());
      setResult(data);
    } catch { toast.error("Could not calculate. Please try again."); }
    finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.post("/zakat/pdf", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "zakat-calculation.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF."); }
  };

  return (
    <div>
      <PageHero
        label="Islamic Giving"
        title="Zakat Calculator"
        subtitle="Estimate your Zakat across cash, gold, silver, investments and business assets in your chosen currency. For informational purposes only."
        crumbs={[{ label: "Islamic Giving", to: "/islamic-giving" }, { label: "Zakat Calculator" }]}
      />

      <section className="bg-sand">
        <Container className="grid gap-10 py-16 lg:grid-cols-[1.6fr_1fr]">
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-emerald-900/10 bg-white p-5">
              <div className="flex-1">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="mt-1.5" data-testid="zakat-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>Nisab basis</Label>
                <div className="mt-1.5 flex rounded-lg border border-emerald-900/15 p-1">
                  {["gold", "silver"].map((b) => (
                    <button key={b} onClick={() => setNisabBasis(b)} data-testid={`nisab-${b}`} className={`flex-1 rounded-md py-1.5 text-sm font-semibold capitalize transition ${nisabBasis === b ? "bg-emerald-900 text-white" : "text-emerald-900"}`}>{b}</button>
                  ))}
                </div>
              </div>
            </div>

            {ASSET_GROUPS.map(([group, fields]) => (
              <div key={group} className="rounded-2xl border border-emerald-900/10 bg-white p-6">
                <h3 className="flex items-center gap-2 font-display text-xl font-bold text-emerald-900"><Coins className="h-5 w-5 text-gold" /> {group}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {fields.map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-xs text-slate-500">{label}</Label>
                      <Input type="number" min="0" placeholder="0" data-testid={`asset-${key}`} value={assets[key] || ""} onChange={(e) => setAssets((a) => ({ ...a, [key]: e.target.value }))} className="mt-1" />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-emerald-900/10 bg-white p-6">
              <h3 className="font-display text-xl font-bold text-emerald-900">Liabilities</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {LIABILITY_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-xs text-slate-500">{label}</Label>
                    <Input type="number" min="0" placeholder="0" data-testid={`liability-${key}`} value={liabilities[key] || ""} onChange={(e) => setLiabilities((l) => ({ ...l, [key]: e.target.value }))} className="mt-1" />
                  </div>
                ))}
              </div>
            </div>

            <button onClick={compute} disabled={loading} data-testid="zakat-calculate-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-900 px-6 py-4 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:opacity-60">
              <Calculator className="h-4 w-4" /> {loading ? "Calculating…" : "Calculate Zakat"}
            </button>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-3xl border border-emerald-900/10 bg-emerald-950 p-7 text-white" data-testid="zakat-result">
              <SectionLabel light>Your Estimate</SectionLabel>
              {result ? (
                <div className="mt-5 space-y-3 text-sm">
                  <Row label="Total Assets" value={`${result.currency} ${result.total_assets.toLocaleString()}`} />
                  <Row label="Liabilities" value={`- ${result.currency} ${result.total_liabilities.toLocaleString()}`} />
                  <Row label="Net Zakatable Wealth" value={`${result.currency} ${result.net_wealth.toLocaleString()}`} bold />
                  <Row label={`Nisab (${result.nisab_basis})`} value={`${result.currency} ${result.nisab_threshold.toLocaleString()}`} />
                  <div className="my-4 h-px bg-white/10" />
                  <div className="rounded-2xl bg-emerald-900/60 p-4">
                    <div className="text-xs uppercase tracking-wider text-gold-light">{result.zakat_due ? "Zakat is due" : "Zakat not due"}</div>
                    <div className="mt-1 font-display text-4xl font-bold">{result.currency} {result.zakat_amount.toLocaleString()}</div>
                    <div className="mt-1 text-xs text-emerald-100/70">at {(result.zakat_rate * 100).toFixed(2)}% of net wealth</div>
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <button onClick={downloadPdf} data-testid="zakat-pdf-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-white hover:bg-gold-light"><Download className="h-4 w-4" /> Download PDF</button>
                    <button onClick={() => toast.success("Email delivery will be available soon.")} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"><Mail className="h-4 w-4" /> Email My Calculation</button>
                  </div>
                  <div className="mt-6 rounded-2xl border border-gold/30 bg-emerald-900/40 p-5 text-center">
                    <p className="font-display text-lg font-bold text-gold-light">Your Zakat is more than a number.</p>
                    <p className="mt-1 text-sm text-emerald-50/80">It is an act of responsibility, purification and care for the community.</p>
                    <button onClick={() => { window.gtag && window.gtag("event", "zakat_calculated", { value: result.zakat_amount, currency: result.currency, zakat_due: result.zakat_due }); navigate("/initiatives/zakat-fund"); }} data-testid="fulfil-zakat-btn" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-white transition hover:bg-gold-light"><HeartHandshake className="h-4 w-4" /> Fulfil Your Zakat <ArrowRight className="h-4 w-4 rtl:rotate-180" /></button>
                    <button onClick={() => { window.gtag && window.gtag("event", "zakat_fund_initiatives", { value: result.zakat_amount, currency: result.currency }); navigate("/programs"); }} data-testid="zakat-fund-initiatives-btn" className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/50 px-6 py-3 text-sm font-bold text-gold-light transition hover:bg-gold/10"><Coins className="h-4 w-4" /> Use Zakat to Fund Initiatives <ArrowRight className="h-4 w-4 rtl:rotate-180" /></button>
                  </div>
                </div>
              ) : (
                <p className="mt-5 text-sm leading-relaxed text-emerald-100/70">Enter your assets and liabilities, then calculate to see your estimated Zakat.</p>
              )}
            </div>

            <div className="mt-4 flex gap-2.5 rounded-2xl border border-gold/30 bg-gold-soft p-4 text-xs leading-relaxed text-emerald-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              This calculator is provided for informational and educational purposes and should not be treated as a religious ruling or financial advice. Users should consult a qualified scholar for personal circumstances.
            </div>
          </aside>
        </Container>
      </section>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-emerald-100/70">{label}</span>
      <span className={bold ? "font-bold" : ""}>{value}</span>
    </div>
  );
}
